import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Volume2, RotateCcw, ChevronRight } from 'lucide-react';
import apiClient from '../../helpers/apiClient';

// ─── Animations ─────────────────────────────────────────────────────────────

const fadeSlideIn = keyframes`from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}`;
const pop = keyframes`0%{transform:scale(0.8)}60%{transform:scale(1.08)}100%{transform:scale(1)}`;
const shake = keyframes`0%,100%{transform:translateX(0)}25%{transform:translateX(-8px)}75%{transform:translateX(8px)}`;
const confettiFall = keyframes`
  0%{opacity:1;transform:translate(0,0) rotate(0deg)}
  100%{opacity:0;transform:translate(var(--dx),120px) rotate(360deg)}
`;
const pulse = keyframes`0%,100%{transform:scale(1)}50%{transform:scale(1.06)}`;
const overlayIn = keyframes`from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}`;

// ─── Types ───────────────────────────────────────────────────────────────────

type QType = 'en-vi' | 'vi-en' | 'listen-word' | 'listen-meaning';

interface QuizQuestion {
  id: number;
  front: string;
  back: string;
  phonetic?: string;
  type: QType;
  options: string[];
  answer: string;
}

// ─── Styled Components ───────────────────────────────────────────────────────

const Wrap = styled.div`
  animation: ${fadeSlideIn} 0.4s ease;
  font-family: 'Segoe UI', sans-serif;
`;

/* Fixed height so feedback overlay never changes layout */
const QuizCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  padding: 20px 24px 14px;
  margin-bottom: 16px;
  text-align: center;
  box-shadow: 0 8px 30px rgba(102,126,234,0.3);
  height: 210px;
  position: relative;
  overflow: hidden;
  display: flex; flex-direction: column;
  align-items: center; justify-content: flex-start;
  animation: ${fadeSlideIn} 0.4s ease;
`;

const QTypeTag = styled.div`
  background: rgba(255,255,255,0.2);
  color: #fff;
  font-size: 11px; font-weight: 700;
  border-radius: 20px; padding: 4px 12px;
  margin-bottom: 14px; text-transform: uppercase; letter-spacing: 1px;
  flex-shrink: 0;
`;

const QText = styled.div`
  font-size: 32px; font-weight: 800; color: #fff;
  line-height: 1.2; margin-bottom: 6px;
`;

const QPhonetic = styled.div`
  font-size: 13px; color: rgba(255,255,255,0.75); font-style: italic;
`;

const ListenBtn = styled.button`
  background: rgba(255,255,255,0.25); border: 2px solid rgba(255,255,255,0.5);
  color: #fff; border-radius: 50px; padding: 9px 22px;
  font-size: 14px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; gap: 8px; margin: 0 auto;
  transition: all 0.2s;
  &:hover { background: rgba(255,255,255,0.4); }
`;

/* Slides up from the bottom of the card — no layout shift */
const CardBottomOverlay = styled.div<{ correct: boolean }>`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  padding: 8px 16px;
  background: ${p => p.correct
    ? 'linear-gradient(90deg, rgba(20,83,45,0.93), rgba(34,197,94,0.88))'
    : 'linear-gradient(90deg, rgba(127,29,29,0.93), rgba(239,68,68,0.88))'};
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px;
  animation: ${overlayIn} 0.25s ease;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
`;

const OverlayFeedback = styled.div`
  font-size: 13px; font-weight: 800; color: #fff;
  white-space: nowrap; flex-shrink: 0;
`;

const OverlayReveal = styled.div`
  font-size: 13px; color: rgba(255,255,255,0.93);
  font-weight: 600; text-align: right;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  flex: 1;
`;

const OptionsGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
  margin-bottom: 16px;
  @media(max-width:600px){ grid-template-columns:1fr; }
`;

interface OptionButtonProps {
  state: 'idle' | 'correct' | 'wrong' | 'show-correct';
}

const OptionBtn = styled.button<OptionButtonProps>`
  padding: 14px 18px;
  border-radius: 14px;
  border: 2.5px solid;
  font-size: 15px; font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  animation: ${fadeSlideIn} 0.3s ease;

  ${({ state }) => state === 'idle' && css`
    border-color: #e2e8f0; background: #fff; color: #334155;
    &:hover { border-color: #667eea; background: #f0f4ff; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(102,126,234,0.15); }
  `}
  ${({ state }) => state === 'correct' && css`
    border-color: #22c55e; background: #dcfce7; color: #166534;
    animation: ${pop} 0.4s ease;
  `}
  ${({ state }) => state === 'wrong' && css`
    border-color: #ef4444; background: #fee2e2; color: #991b1b;
    animation: ${shake} 0.4s ease;
  `}
  ${({ state }) => state === 'show-correct' && css`
    border-color: #22c55e; background: #dcfce7; color: #166534;
  `}
`;

const ProgressBar = styled.div<{ pct: number }>`
  height: 7px; border-radius: 4px;
  background: #e2e8f0;
  margin-bottom: 16px; overflow: hidden;
  &::after {
    content: ''; display: block; height: 100%;
    width: ${p => p.pct}%;
    background: linear-gradient(90deg, #667eea, #a78bfa);
    border-radius: 4px; transition: width 0.4s ease;
  }
`;

const Counter = styled.div`
  font-size: 13px; color: #94a3b8; font-weight: 600;
  text-align: center; margin-bottom: 8px;
`;

const NextBtn = styled.button`
  background: linear-gradient(90deg, #667eea, #764ba2);
  color: #fff; border: none; border-radius: 12px;
  padding: 12px 32px; font-size: 15px; font-weight: 700;
  cursor: pointer; display: flex; align-items: center; gap: 8px; margin: 0 auto;
  transition: all 0.2s;
  &:hover { opacity: 0.9; transform: translateY(-2px); }
`;

const ResultBox = styled.div`
  text-align: center; padding: 40px 20px;
  background: #fff; border-radius: 20px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  animation: ${pop} 0.5s ease;
`;

const ResultEmoji = styled.div`
  font-size: 64px; margin-bottom: 12px;
  animation: ${pulse} 1.5s infinite;
`;

const ResultScore = styled.div`
  font-size: 48px; font-weight: 900;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ResultMsg = styled.div`
  font-size: 18px; font-weight: 600; color: #334155; margin: 8px 0 24px;
`;

const RestartBtn = styled.button`
  background: linear-gradient(90deg, #667eea, #764ba2);
  color: #fff; border: none; border-radius: 12px;
  padding: 14px 36px; font-size: 16px; font-weight: 700;
  cursor: pointer; display: flex; align-items: center; gap: 8px; margin: 0 auto;
  transition: all 0.2s;
  &:hover { opacity: 0.9; transform: translateY(-2px); }
`;

const Confetti = styled.div<{ dx: number; color: string; delay: number }>`
  position: fixed;
  top: 0; left: ${p => Math.random() * 100}vw;
  width: 10px; height: 10px;
  background: ${p => p.color};
  border-radius: 2px;
  pointer-events: none; z-index: 9999;
  animation: ${confettiFall} ${() => 1.5 + Math.random()}s linear ${p => p.delay}ms forwards;
  --dx: ${p => p.dx}px;
`;

// ─── Sound helper ─────────────────────────────────────────────────────────────

function playBeep(freq: number, duration: number, type: OscillatorType = 'sine') {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = freq;
    osc.type = type;
    gain.gain.setValueAtTime(0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.start(); osc.stop(ctx.currentTime + duration);
  } catch {}
}

function playCorrect() { playBeep(880, 0.15); setTimeout(() => playBeep(1320, 0.15), 120); }
function playWrong() { playBeep(220, 0.4, 'sawtooth'); }
function playComplete() {
  [523, 659, 784, 1047].forEach((f, i) => setTimeout(() => playBeep(f, 0.2), i * 100));
}

// ─── TTS ─────────────────────────────────────────────────────────────────────

async function speakWord(word: string) {
  try {
    const res = await apiClient.get(`/api/flashcards/tts/${encodeURIComponent(word)}`);
    if (res.data?.audio_url) {
      const base = process.env.REACT_APP_API_URL || '';
      const url = res.data.audio_url.startsWith('http') ? res.data.audio_url : `${base}${res.data.audio_url}`;
      await new Audio(url).play();
      return;
    }
  } catch {}
  if ('speechSynthesis' in window) {
    const u = new SpeechSynthesisUtterance(word);
    u.lang = 'en-US';
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  }
}

// ─── Question generator ───────────────────────────────────────────────────────

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildQuestions(items: any[]): QuizQuestion[] {
  if (items.length < 2) return [];
  const types: QType[] = ['en-vi', 'vi-en', 'listen-word', 'listen-meaning'];
  const qs: QuizQuestion[] = [];

  items.forEach(item => {
    const type = types[Math.floor(Math.random() * types.length)];
    const otherItems = shuffle(items.filter(x => x.id !== item.id)).slice(0, 3);

    let options: string[] = [];
    let answer = '';

    if (type === 'en-vi' || type === 'listen-meaning') {
      answer = item.back;
      options = shuffle([answer, ...otherItems.map((x: any) => x.back)]);
    } else {
      answer = item.front;
      options = shuffle([answer, ...otherItems.map((x: any) => x.front)]);
    }

    qs.push({ id: item.id, front: item.front, back: item.back, phonetic: item.phonetic, type, options, answer });
  });

  return shuffle(qs);
}

const QTYPE_LABEL: Record<QType, string> = {
  'en-vi': '📖 Chọn nghĩa tiếng Việt',
  'vi-en': '🔤 Chọn từ tiếng Anh',
  'listen-word': '🎧 Nghe → chọn từ',
  'listen-meaning': '🎧 Nghe → chọn nghĩa',
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
  items: any[];
  onGameComplete?: (pct: number) => void;
}

const ReviewQuizGame: React.FC<Props> = ({ items, onGameComplete }) => {
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const start = useCallback(() => {
    setQuestions(buildQuestions(items));
    setIdx(0); setSelected(null); setAnswered(false);
    setScore(0); setDone(false); setShowConfetti(false);
  }, [items]);

  useEffect(() => { start(); }, [start]);

  // Report result when game ends
  useEffect(() => {
    if (done && questions.length > 0 && onGameComplete) {
      onGameComplete(Math.round((score / questions.length) * 100));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const q = questions[idx];

  // Auto-play listen types
  useEffect(() => {
    if (!q) return;
    if (q.type === 'listen-word' || q.type === 'listen-meaning') {
      const t = setTimeout(() => speakWord(q.front), 400);
      return () => clearTimeout(t);
    }
  }, [q]);

  const pick = (opt: string) => {
    if (answered) return;
    setSelected(opt);
    setAnswered(true);
    if (opt === q.answer) {
      playCorrect();
      setScore(s => s + 1);
    } else {
      playWrong();
    }
  };

  const next = () => {
    if (idx + 1 >= questions.length) {
      setDone(true);
      playComplete();
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    } else {
      setIdx(i => i + 1);
      setSelected(null);
      setAnswered(false);
    }
  };

  const optState = (opt: string): 'idle' | 'correct' | 'wrong' | 'show-correct' => {
    if (!answered) return 'idle';
    if (opt === q.answer) return 'correct';
    if (opt === selected) return 'wrong';
    return 'idle';
  };

  if (!questions.length) return (
    <div style={{ textAlign: 'center', color: '#bbb', padding: 40 }}>
      Cần ít nhất 2 từ vựng để chơi trắc nghiệm.
    </div>
  );

  const pct = Math.round((idx / questions.length) * 100);
  const resultPct = Math.round((score / questions.length) * 100);

  if (done) {
    const emoji = resultPct >= 80 ? '🎉' : resultPct >= 50 ? '👍' : '💪';
    const msg = resultPct >= 80 ? 'Xuất sắc! Em giỏi lắm!' : resultPct >= 50 ? 'Tốt lắm! Cố thêm chút nữa nhé!' : 'Cố gắng hơn nhé! Em làm được!';
    return (
      <Wrap>
        {showConfetti && Array.from({ length: 30 }).map((_, i) => (
          <Confetti key={i} dx={(Math.random() - 0.5) * 300} color={['#667eea','#f59e0b','#22c55e','#ec4899','#a78bfa'][i % 5]} delay={i * 60} />
        ))}
        <ResultBox>
          <ResultEmoji>{emoji}</ResultEmoji>
          <ResultScore>{score}/{questions.length}</ResultScore>
          <ResultMsg>{msg}</ResultMsg>
          <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 24 }}>
            Tỉ lệ đúng: <strong style={{ color: resultPct >= 80 ? '#22c55e' : '#f59e0b' }}>{resultPct}%</strong>
          </div>
          <RestartBtn onClick={start}><RotateCcw size={16} /> Chơi lại</RestartBtn>
        </ResultBox>
      </Wrap>
    );
  }

  const isListenType = q.type === 'listen-word' || q.type === 'listen-meaning';
  const revealText = isListenType
    ? `🔤 ${q.front}${q.phonetic ? ` /${q.phonetic.replace(/^\/|\/$/g, '')}/` : ''}`
    : `Đáp án: ${q.answer}`;

  return (
    <Wrap>
      <Counter>Câu {idx + 1} / {questions.length} &nbsp;·&nbsp; 🌟 {score} điểm</Counter>
      <ProgressBar pct={pct} />

      {/* Fixed-height card — feedback overlay slides up inside, never resizes */}
      <QuizCard>
        <QTypeTag>{QTYPE_LABEL[q.type]}</QTypeTag>

        {isListenType ? (
          <>
            <div style={{ fontSize: 38, marginBottom: 10 }}>🎵</div>
            <ListenBtn onClick={() => speakWord(q.front)}>
              <Volume2 size={16} /> Nghe lại
            </ListenBtn>
          </>
        ) : q.type === 'en-vi' ? (
          <>
            <QText>{q.front}</QText>
            {q.phonetic && <QPhonetic>/{q.phonetic.replace(/^\/|\/$/g, '')}/</QPhonetic>}
          </>
        ) : (
          <QText>{q.back}</QText>
        )}

        {/* Bottom overlay — position:absolute so card height stays fixed */}
        {answered && (
          <CardBottomOverlay correct={selected === q.answer}>
            <OverlayFeedback>
              {selected === q.answer ? '✅ Đúng!' : '❌ Sai!'}
            </OverlayFeedback>
            <OverlayReveal>{revealText}</OverlayReveal>
          </CardBottomOverlay>
        )}
      </QuizCard>

      <OptionsGrid>
        {q.options.map((opt, i) => (
          <OptionBtn key={opt + i} state={optState(opt)} onClick={() => pick(opt)} disabled={answered}>
            <span style={{ opacity: 0.5, marginRight: 8 }}>{['A','B','C','D'][i]}.</span> {opt}
          </OptionBtn>
        ))}
      </OptionsGrid>

      {answered && (
        <NextBtn onClick={next}>
          {idx + 1 >= questions.length ? '🎯 Xem kết quả' : 'Câu tiếp theo'}
          <ChevronRight size={18} />
        </NextBtn>
      )}
    </Wrap>
  );
};

export default ReviewQuizGame;
